import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Trash2, CheckSquare, Square, X, Sparkles, Loader2, Brain } from "lucide-react";
import { format } from "date-fns";
import {
  Search,
  Filter,
  Image,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Grid,
  List,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import PhotoGrid from "@/components/photos/PhotoGrid";
import PhotoDetailModal from "@/components/photos/PhotoDetailModal";

export default function Photos() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [inspectorFilter, setInspectorFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  const queryClient = useQueryClient();

  const { data: photos = [], isLoading: loadingPhotos } = useQuery({
    queryKey: ["photos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("photos")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });

  const { data: inspectors = [] } = useQuery({
    queryKey: ["inspectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles_with_email")
        .select("*")
        .eq("role", "inspector");
      if (error) throw error;
      // Map to expected structure
      return data.map(profile => ({
        ...profile,
        name: profile.full_name,
      }));
    },
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .order("scheduled_start", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const isLoading = loadingPhotos;

  const assignMutation = useMutation({
    mutationFn: async ({ photoId, jobId }) => {
      const job = jobs.find((j) => j.id === jobId);
      const { error } = await supabase
        .from("photos")
        .update({
          job_id: jobId,
          property_id: job?.property_id,
          assignment_status: "manual_assigned",
        })
        .eq("id", photoId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["photos"] });
      setSelectedPhoto(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => {
      const idArray = Array.isArray(ids) ? ids : [ids];
      const { error } = await supabase
        .from("photos")
        .delete()
        .in("id", idArray);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["photos"] });
      setSelectedIds(new Set());
      setSelectMode(false);
      setSelectedPhoto(null);
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async ({ photoIds, userContext }) => {
      const idArray = Array.isArray(photoIds) ? photoIds : [photoIds];
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-photo`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            photo_ids: idArray,
            user_context: userContext || null,
          }),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Analysis failed");
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["photos"] });
      queryClient.invalidateQueries({ queryKey: ["photo-defects"] });
      setSelectedIds(new Set());
      setSelectMode(false);
      
      const successCount = data.results?.filter(r => r.success).length || 0;
      const totalDefects = data.results?.reduce((sum, r) => sum + (r.defects_found || 0), 0) || 0;
      
      toast.success(`Analyzed ${successCount} photo(s)`, {
        description: `Found ${totalDefects} defect(s)`,
      });
    },
    onError: (error) => {
      toast.error("Analysis failed", {
        description: error.message,
      });
    },
  });

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredPhotos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPhotos.map(p => p.id)));
    }
  };

  const filteredPhotos = photos.filter((photo) => {
    const matchesSearch =
      photo.inspector_name?.toLowerCase().includes(search.toLowerCase()) ||
      photo.source_dropbox_path?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || photo.assignment_status === statusFilter;
    const matchesInspector = inspectorFilter === "all" || photo.inspector_id === inspectorFilter;
    const matchesJob = jobFilter === "all" || 
                       (jobFilter === "unassigned" ? !photo.job_id : photo.job_id === jobFilter);
    return matchesSearch && matchesStatus && matchesInspector && matchesJob;
  });

  const statusCounts = {
    all: photos.length,
    auto_matched: photos.filter((p) => p.assignment_status === "auto_matched").length,
    manual_assigned: photos.filter((p) => p.assignment_status === "manual_assigned").length,
    unassigned: photos.filter((p) => p.assignment_status === "unassigned").length,
    out_of_range: photos.filter((p) => p.assignment_status === "out_of_range").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Photos</h1>
          <p className="text-slate-500 mt-1">View and manage inspection photos</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("grid")}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="icon"
            onClick={() => setViewMode("list")}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {statusCounts.auto_matched + statusCounts.manual_assigned}
              </p>
              <p className="text-xs text-slate-500">Matched</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <HelpCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{statusCounts.unassigned}</p>
              <p className="text-xs text-slate-500">Unassigned</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-100">
              <AlertCircle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{statusCounts.out_of_range}</p>
              <p className="text-xs text-slate-500">Out of Range</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-slate-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Image className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{statusCounts.all}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search photos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="auto_matched">Auto Matched</SelectItem>
            <SelectItem value="manual_assigned">Manual</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            <SelectItem value="out_of_range">Out of Range</SelectItem>
          </SelectContent>
        </Select>
        <Select value={inspectorFilter} onValueChange={setInspectorFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Inspector" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Inspectors</SelectItem>
            {inspectors.map((inspector) => (
              <SelectItem key={inspector.id} value={inspector.id}>
                {inspector.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Filter by Job" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jobs</SelectItem>
            <SelectItem value="unassigned">Unassigned to Job</SelectItem>
            {jobs.map((job) => (
              <SelectItem key={job.id} value={job.id}>
                <div className="flex flex-col">
                  <span className="font-medium">{job.property_name || "Unknown Property"}</span>
                  {job.scheduled_start && (
                    <span className="text-xs text-slate-500">
                      {format(new Date(job.scheduled_start), "MMM d, yyyy")}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
        {selectMode ? (
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              onClick={() =>
                analyzeMutation.mutate({
                  photoIds: [...selectedIds],
                  userContext: null,
                })
              }
              disabled={selectedIds.size === 0 || analyzeMutation.isPending}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
            >
              {analyzeMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Brain className="w-4 h-4" />
              )}
              Analyze ({selectedIds.size})
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => deleteMutation.mutate([...selectedIds])}
              disabled={selectedIds.size === 0 || deleteMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedIds.size})
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setSelectMode(true)}
            className="gap-2"
          >
            <CheckSquare className="w-4 h-4" />
            Select
          </Button>
        )}
        </div>
      </div>

      {/* Photos */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array(10)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="aspect-square bg-slate-200 rounded-xl animate-pulse" />
            ))}
        </div>
      ) : filteredPhotos.length === 0 ? (
        <div className="text-center py-16">
          <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No photos found</p>
        </div>
      ) : viewMode === "grid" ? (
        <PhotoGrid 
          photos={filteredPhotos} 
          onPhotoClick={selectMode ? (photo) => toggleSelect(photo.id) : setSelectedPhoto}
          selectMode={selectMode}
          selectedIds={selectedIds}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Photo
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Inspector
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Taken At
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  GPS
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPhotos.map((photo) => (
                <tr
                  key={photo.id}
                  onClick={() => setSelectedPhoto(photo)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100">
                      {photo.thumbnail_url || photo.file_url ? (
                        <img
                          src={photo.thumbnail_url || photo.file_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-5 h-5 text-slate-300" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{photo.inspector_name || "Unknown"}</td>
                  <td className="px-6 py-4 text-slate-600">
                    {photo.taken_at ? format(new Date(photo.taken_at), "PPp") : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      className={`border-0 ${
                        photo.assignment_status === "auto_matched" ||
                        photo.assignment_status === "manual_assigned"
                          ? "bg-emerald-100 text-emerald-700"
                          : photo.assignment_status === "out_of_range"
                          ? "bg-rose-100 text-rose-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {photo.assignment_status?.replace("_", " ") || "unassigned"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-slate-600 text-sm">
                    {photo.gps_lat && photo.gps_lng
                      ? `${photo.gps_lat.toFixed(4)}, ${photo.gps_lng.toFixed(4)}`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Photo Detail Modal */}
      <PhotoDetailModal
        photo={selectedPhoto}
        jobs={jobs}
        onClose={() => setSelectedPhoto(null)}
        onAssign={(photoId, jobId) => assignMutation.mutate({ photoId, jobId })}
        isAssigning={assignMutation.isPending}
        onDelete={(photoId) => deleteMutation.mutate(photoId)}
        isDeleting={deleteMutation.isPending}
        onAnalyze={(photoId, userContext) =>
          analyzeMutation.mutate({ photoIds: [photoId], userContext })
        }
        isAnalyzing={analyzeMutation.isPending}
      />
    </div>
  );
}